import { useState, useEffect, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { classNames } from '~/utils/classNames';
import { workbenchStore } from '~/lib/stores/workbench';
import { capture } from '~/lib/posthog';
import { ANALYTICS_EVENTS } from '@dunea/shared/analytics/events';
import { AdminClaimControls } from './AdminClaimControls';
import { getShareLink } from './preview-url';

interface SharePreviewButtonProps {
  chatId: string;
  /*
   * Pass on ONE mounted instance (the preview toolbar) to get the one-time
   * "share it" callout when a build finishes: false while building, true after.
   */
  buildDone?: boolean;
}

/** Once per browser: a builder who has seen the callout knows where Share is. */
const SHARE_NUDGE_KEY = 'share-nudge:seen';
const SHARE_NUDGE_MS = 12_000;

function shareNudgeSeen(): boolean {
  try {
    return localStorage.getItem(SHARE_NUDGE_KEY) != null;
  } catch {
    return true;
  }
}

/*
 * The thumbnail renders the live preview inside a fixed desktop viewport and
 * scales it down to fit the card — a miniature browser viewport. Rendering at a
 * real desktop size (not the card's tiny pixel box) means the page lays out as
 * it would on a laptop, so the COMPLETE hero section is visible, scaled
 * proportionally, with the website's aspect ratio preserved (never cropped,
 * scrolled, or stretched).
 */
const VIEWPORT_WIDTH = 1280;
const VIEWPORT_HEIGHT = 800;

function SocialButton({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className="w-9 h-9 rounded-full bg-raised border border-hairline flex items-center justify-center text-muted hover:text-primary hover:bg-sunken hover:border-subtle transition-all"
    >
      {icon}
    </button>
  );
}

export function SharePreviewButton({ chatId, buildDone }: SharePreviewButtonProps) {
  const [open, setOpen] = useState(false);
  const [nudge, setNudge] = useState(false);
  const wasBuildDoneRef = useRef(buildDone);
  const nudgePendingRef = useRef(false);
  const [copied, setCopied] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [scale, setScale] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const previews = useStore(workbenchStore.previews);
  const activePreview = previews[0];

  // Load the live preview in the thumbnail. baseUrl is the same origin the main
  // workbench iframe renders, so it frames without X-Frame-Options issues.
  const iframeSrc = activePreview?.baseUrl || previewUrl || undefined;

  // Share the sandbox's own preview host ({chatId}.preview.dunea.app). Once
  // the sandbox sleeps the wildcard fallback forwards it to /p/, which serves
  // the captured build — see getShareLink.
  useEffect(() => {
    setPreviewUrl(activePreview?.baseUrl ? getShareLink(activePreview.baseUrl, chatId, window.location.origin) : null);
  }, [activePreview?.baseUrl, chatId]);

  // The callout: only on the building → done flip (a finished build in this
  // session, not reopening an old project), only with a link to share. A flip
  // that lands before the link stays pending until the link arrives.
  useEffect(() => {
    if (wasBuildDoneRef.current === false && buildDone === true) {
      nudgePendingRef.current = true;
    } else if (buildDone === false) {
      nudgePendingRef.current = false;
    }

    wasBuildDoneRef.current = buildDone;

    if (!nudgePendingRef.current || !previewUrl) {
      return;
    }

    nudgePendingRef.current = false;

    if (shareNudgeSeen()) {
      return;
    }

    try {
      localStorage.setItem(SHARE_NUDGE_KEY, String(Date.now()));
    } catch {
      // storage unavailable — show it anyway, just not remembered
    }

    capture(ANALYTICS_EVENTS.SHARE_NUDGE_SHOWN, { chatId });
    setNudge(true);
  }, [buildDone, previewUrl, chatId]);

  useEffect(() => {
    if (!nudge) {
      return undefined;
    }

    const timeoutId = setTimeout(() => setNudge(false), SHARE_NUDGE_MS);

    return () => clearTimeout(timeoutId);
  }, [nudge]);

  // Close on outside click
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    function handleBlur() {
      if (document.activeElement?.tagName === 'IFRAME') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClick);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('blur', handleBlur);
    };
  }, [open]);

  // Measure the card frame and derive the scale that fits a full 1280px-wide
  // desktop viewport into it. Re-measures on resize so the mini viewport stays
  // proportional at any dialog width.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const el = frameRef.current;

    if (!el) {
      return undefined;
    }

    const measure = () => setScale(el.clientWidth / VIEWPORT_WIDTH);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => observer.disconnect();
  }, [open]);

  // Reset the fade-in whenever the source or dialog visibility changes.
  useEffect(() => {
    setFrameLoaded(false);
  }, [iframeSrc, open]);

  /* Every share path funnels through here so `preview_shared` can never drift
   * from the actions that actually share. `via` is what makes the event useful:
   * it says which channel the loop runs on, which decides where to invest. */
  const trackShare = (via: 'copy' | 'twitter' | 'linkedin' | 'reddit') => {
    capture(ANALYTICS_EVENTS.PREVIEW_SHARED, { chatId, via });
  };

  const handleCopy = () => {
    if (!previewUrl) {
      return;
    }

    navigator.clipboard.writeText(previewUrl);
    trackShare('copy');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareText = 'Check out what I built with Dunea';

  const shareOnTwitter = () => {
    trackShare('twitter');
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(previewUrl || '')}`,
      '_blank',
    );
  };

  const shareOnLinkedIn = () => {
    trackShare('linkedin');
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(previewUrl || '')}`,
      '_blank',
    );
  };

  const shareOnReddit = () => {
    trackShare('reddit');
    window.open(
      `https://reddit.com/submit?url=${encodeURIComponent(previewUrl || '')}&title=${encodeURIComponent(shareText)}`,
      '_blank',
    );
  };

  // Don't render if no preview at all
  if (!previewUrl && !open) {
    return null;
  }

  const displayUrl = previewUrl?.replace(/^https?:\/\//, '') || '';

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => {
          // Only the open transition is intent; closing says nothing. The gap
          // between this and preview_shared is the share panel's drop-off.
          if (!open) {
            capture(ANALYTICS_EVENTS.PREVIEW_SHARE_OPENED, { chatId, fromNudge: nudge });
          }

          setNudge(false);
          setOpen(!open);
        }}
        className={classNames('ui-btn-icon', (open || nudge) && 'text-primary bg-white/[0.04]')}
        title="Share preview"
      >
        <div className="i-ph:share-network text-base" />
      </button>

      {nudge && !open && (
        <button
          type="button"
          onClick={() => {
            capture(ANALYTICS_EVENTS.PREVIEW_SHARE_OPENED, { chatId, fromNudge: true });
            setNudge(false);
            setOpen(true);
          }}
          className={classNames(
            'absolute right-0 top-full mt-2 z-[9999] w-[240px] text-left',
            'bg-surface border border-accent/25 rounded-xl px-3.5 py-3',
            'shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)]',
            'origin-top-right animate-in fade-in-0 zoom-in-95 slide-in-from-top-1 duration-200 ease-out',
          )}
        >
          <span className="flex items-center gap-2 text-[13px] font-medium text-primary">
            <span className="i-ph:sparkle text-accent" />
            Your site is ready
          </span>
          <span className="mt-1 block text-[12px] leading-[1.5] text-muted">
            Send the link to someone and get a first reaction.
          </span>
        </button>
      )}

      {open && (
        <div
          className={classNames(
            'absolute right-0 top-full mt-2 z-[9999]',
            'w-[400px] max-w-[calc(100vw-1.5rem)]',
            'bg-surface border border-subtle rounded-2xl',
            'shadow-[0_40px_90px_-30px_rgba(0,0,0,0.9),0_0_0_1px_rgba(255,255,255,0.02)]',
            'overflow-hidden',
            'origin-top-right animate-in fade-in-0 zoom-in-95 slide-in-from-top-1 duration-200 ease-out',
          )}
        >
          {/* Header — title + subtitle (left), close (right) */}
          <div className="flex items-start justify-between gap-3 px-4 pt-3.5 pb-3 border-b border-hairline">
            <div className="min-w-0">
              <h2 className="text-[15px] font-semibold text-primary font-geist tracking-[-0.01em]">Share Preview</h2>
              <p className="text-[12px] text-muted mt-0.5">Create a temporary preview link</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              title="Close"
              className="-mr-1 -mt-0.5 w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full text-muted hover:text-primary hover:bg-white/[0.04] transition-colors"
            >
              <div className="i-ph:x text-sm" />
            </button>
          </div>

          <div className="flex flex-col gap-3 p-3.5">
            {/*
             * Preview card — the hero element. A miniature browser viewport: the live
             * preview renders in an iframe sized to a full 1280x800 desktop viewport,
             * then CSS-scaled to fit. Because the page lays out at real desktop
             * dimensions before scaling, the COMPLETE hero section shows, proportionally
             * shrunk, aspect ratio preserved — no crop, no scroll, no distortion. The
             * iframe is non-interactive (pointer-events off). The 1280:800 frame keeps
             * the mini viewport exact.
             */}
            <div
              ref={frameRef}
              className="relative rounded-xl border border-hairline overflow-hidden bg-gradient-to-b from-raised to-sunken"
              style={{ aspectRatio: `${VIEWPORT_WIDTH} / ${VIEWPORT_HEIGHT}` }}
            >
              {(!frameLoaded || scale === 0) && <div className="absolute inset-0 bg-raised animate-pulse" />}

              {iframeSrc ? (
                <iframe
                  src={iframeSrc}
                  title="Website preview"
                  aria-hidden="true"
                  tabIndex={-1}
                  onLoad={() => setFrameLoaded(true)}
                  className={classNames(
                    'absolute top-0 left-0 origin-top-left border-0 overflow-hidden pointer-events-none select-none transition-opacity duration-300 ease-out',
                    frameLoaded && scale > 0 ? 'opacity-100' : 'opacity-0',
                  )}
                  style={{
                    width: VIEWPORT_WIDTH,
                    height: VIEWPORT_HEIGHT,
                    transform: `scale(${scale})`,
                  }}
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 px-6 text-center">
                  <div className="w-10 h-10 rounded-xl bg-surface border border-hairline flex items-center justify-center">
                    <div className="i-ph:globe-simple text-muted text-xl" />
                  </div>
                  <span className="font-geist-mono text-[11px] text-secondary truncate max-w-full">
                    {displayUrl || 'your site'}
                  </span>
                </div>
              )}

              {/* Made with Dunea — overlay badge, bottom-right corner (out of hero content) */}
              <div className="absolute bottom-2 right-2 z-[2] inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-page/70 backdrop-blur-md border border-white/10 shadow-[0_2px_10px_-2px_rgba(0,0,0,0.6)]">
                <img src="/dunea-mark.svg" alt="" className="w-3 h-3" />
                <span className="text-[10px] font-medium text-white/90 font-geist leading-none">Made with Dunea</span>
              </div>
            </div>

            {/* Preview URL — label + copy bar */}
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium text-muted uppercase tracking-[0.04em]">Preview URL</span>
              <div className="flex items-center gap-2 bg-sunken border border-hairline rounded-lg pl-3.5 pr-1.5 py-1.5">
                <span className="text-[13px] text-secondary truncate flex-1 text-left font-geist-mono">
                  {displayUrl}
                </span>
                <button
                  onClick={handleCopy}
                  className={classNames(
                    'flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                    copied
                      ? 'bg-accent/15 text-accent'
                      : 'bg-raised text-secondary hover:bg-white/[0.06] hover:text-primary',
                  )}
                >
                  <div className={copied ? 'i-ph:check text-sm' : 'i-ph:copy text-sm'} />
                  {copied ? 'Copied' : 'Copy link'}
                </button>
              </div>
            </div>

            {/* Social sharing */}
            <div className="flex items-center justify-center gap-2.5">
              <SocialButton
                label="Share on X"
                onClick={shareOnTwitter}
                icon={
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                }
              />
              <SocialButton
                label="Share on LinkedIn"
                onClick={shareOnLinkedIn}
                icon={<div className="i-ph:linkedin-logo text-lg" />}
              />
              <SocialButton
                label="Share on Reddit"
                onClick={shareOnReddit}
                icon={<div className="i-ph:reddit-logo text-lg" />}
              />
            </div>

            {/* Admin-only: hand the generation off to a user via a claim link (DEV-169). */}
            <AdminClaimControls chatId={chatId} />

            {/* Footer — divider + temporary-preview info, identical in local dev and production */}
            <div className="-mx-3.5 mt-0.5 px-3.5 pt-3.5 border-t border-hairline">
              <div className="flex items-start gap-2">
                <div className="i-ph:info text-[14px] text-warning mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[12px] font-medium text-primary">Temporary preview</p>
                  <p className="text-[12px] text-muted leading-[1.55] mt-0.5">
                    Links automatically expire after 30 minutes of inactivity.
                    <br />
                    <span className="text-secondary font-medium">Publish</span> to create a permanent URL.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
